import React, { useMemo, useRef, useState } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { InfoNoticeElement } from "@front-shared/elements/infoNotice.element"
import { NirvanaBreathingLoaderElement } from "@front-shared/elements/nirvanaBreathingLoader.element"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { NETWORK_STATUS_CONFIG } from "../../../config/networkStatus.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useClientPaginatedReview } from "../../../hooks/clientPaginatedReview.hook"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { useScrollableElementBoundaries } from "../../../hooks/scrollableElementBoundaries.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { networkStatusCreateRulesFromReview } from "../../../store/thunks/networkStatus.thunks"
import type { IReviewNewRulesLocationState } from "../../../types/networkStatus.types"
import { CsvReviewFloatingControlComponent } from "../../general/csvUploads/csvReviewFloatingControl.component"
import { CsvReviewPageHeaderComponent } from "../../general/csvUploads/csvReviewPageHeader.component"
import { PoliciesListPaginationComponent } from "../../policies/policiesList/policiesListPagination.component"
import { NetworkStatusRulesBulkUploadPopupComponent } from "../../popups/networkStatusRulesBulkUploadPopup.component"
import { RulesTableComponent } from "../rulesList/rulesTable.component"

export const ReviewNewRulesComponent: React.FunctionComponent<
    IReviewNewRulesComponentProps
> = props => {
    useScrollFix()

    useHasPermissions(
        OneVerifyPermission.manageNetworkStatusRules,
        undefined,
        true,
    )

    useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_NETWORK_STATUS_RULES,
        undefined,
        ROUTES_CONFIG.profileUrl,
    )

    const dispatch = useAppDispatch()

    const scrollableTableRef = useRef<HTMLDivElement>(null)

    const scrollableTableBoundaries =
        useScrollableElementBoundaries(scrollableTableRef)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const isSubmitting = useMemo<boolean>(() => {
        return RuntimeHelper.isNetworkStatusCreateRulesFromReviewLoading()
    }, [runtimeState.isLoading])

    const [isReuploadPopupActive, setIsReuploadPopupActive] =
        useState<boolean>(false)

    const {
        getNewItems,
        nextPagination,
        pageItems: pageRules,
        resetReviewStart,
        selectedPaginationOption,
        setSelectedPaginationOption,
        validItems: validRulesToSend,
    } = useClientPaginatedReview(
        props.state.rules,
        props.state.errors,
        NETWORK_STATUS_CONFIG.reviewListPerPageStorageKey,
    )

    const onReuploadCsv = () => {
        setIsReuploadPopupActive(true)
    }

    const onReuploadFromPopupDone = (data: IReviewNewRulesLocationState) => {
        setIsReuploadPopupActive(false)
        resetReviewStart()

        props.onReuploadDone(data)
    }

    const onAddValidRules = () => {
        if (!selectedPracticeRole || !validRulesToSend.length) {
            return
        }

        ConfirmationHelper.enableConfirmation(
            () => {
                dispatch(
                    networkStatusCreateRulesFromReview({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            rules: validRulesToSend,
                        },
                        onSuccess: props.onAddValidRulesSuccess,
                    }),
                )
            },
            `Are you sure you want to create a new version?`,
            [
                `Creating a new version with the valid rules will make it the active version for the practice.`,

                <InfoNoticeElement className="text-left">
                    {NETWORK_STATUS_CONFIG.bulkCreateDurationNotice}
                </InfoNoticeElement>,
            ],
        )
    }

    return (
        <div className="pb-32 relative">
            <NirvanaBreathingLoaderElement
                isActive={isSubmitting}
                subContent={
                    <InfoNoticeElement>
                        {NETWORK_STATUS_CONFIG.bulkCreateDurationNotice}
                    </InfoNoticeElement>
                }
            />

            <PageHelmetElement
                title="Review New Rules"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <CsvReviewPageHeaderComponent
                className="px-[24px]"
                style={{
                    right: scrollableTableBoundaries?.x,
                }}
                title="Review New Rules"
                filename={props.state.filename}
                onClose={props.onClose}
            />

            <div className="relative pb-[120px]">
                <RulesTableComponent
                    scrollableElementRef={scrollableTableRef}
                    rules={pageRules}
                    errors={props.state.errors}
                    showIdColumn={false}
                />

                <PoliciesListPaginationComponent
                    className="md:px-[24px] px-[48px]"
                    style={{
                        right: scrollableTableBoundaries?.x,
                    }}
                    getNewItems={getNewItems}
                    nextPagination={nextPagination}
                    onPaginationChange={undefined}
                    selectedPaginationOption={selectedPaginationOption}
                    setSelectedPaginationOption={setSelectedPaginationOption}
                />
            </div>

            <CsvReviewFloatingControlComponent
                invalidCount={props.state.errors.length}
                totalCount={
                    props.state.totalRowCount ?? props.state.rules.length
                }
                validCount={validRulesToSend.length}
                onReuploadCsv={onReuploadCsv}
                onAddValidRows={onAddValidRules}
                isSubmitting={isSubmitting}
            />

            <NetworkStatusRulesBulkUploadPopupComponent
                isActive={isReuploadPopupActive}
                onDone={() => {
                    setIsReuploadPopupActive(false)
                }}
                onValidationDone={onReuploadFromPopupDone}
            />
        </div>
    )
}

export interface IReviewNewRulesComponentProps {
    state: IReviewNewRulesLocationState
    onClose: () => void
    onReuploadDone: (data: IReviewNewRulesLocationState) => void
    onAddValidRulesSuccess: () => void
}
